import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart, Plus, Loader2, ArrowUpRight, ShieldCheck, ShieldAlert,
    Layers, Coins, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useFideicomiso } from "../../lib/fideicomiso";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const TIPOS = ["senior", "mezzanine", "equity", "warehouse", "revolving"];
const MONEDAS = ["MXN", "USD", "EUR"];
const AMORT = ["bullet", "frances", "aleman", "custom"];

const ESTADO_BADGE = {
    ratificada: "badge-gold",
    disponible: "badge-gold",
    dispuesta: "badge-exito",
    amortizando: "badge-gold",
    pagada: "badge-exito",
    en_default: "badge-error",
    reestructurada: "badge-mock",
};

function money(n, cur = "MXN") {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("es-MX", { style: "currency", currency: cur, maximumFractionDigits: 0 });
}

export default function Facilities() {
    const { selected } = useFideicomiso();
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        numero: "", nombre_comercial: "", tipo: "senior", moneda: "MXN",
        monto_comprometido: "", fecha_efectiva: "", fecha_vencimiento: "",
        tasa_base: "TIIE", spread_pct: "", amortizacion: "bullet",
        lender_principal_persona_id: "",
    });

    const canWrite = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.ADMIN_TI]);

    const load = async () => {
        if (!selected) return;
        setLoading(true);
        try {
            const [{ data: fs }, { data: ps }] = await Promise.all([
                api.get("/facilities", { params: { fideicomiso_id: selected.id } }),
                api.get("/personas", { params: { tipo: "moral" } }).catch(() => ({ data: [] })),
            ]);
            setRows(fs);
            setPersonas(ps);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.lender_principal_persona_id) {
            return toast.error("Selecciona un lender principal");
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                monto_comprometido: parseFloat(form.monto_comprometido || 0),
                spread_pct: parseFloat(form.spread_pct || 0),
                colenders: [],
                documentos_contractuales: [],
                garantia_activa: true,
            };
            await api.post(`/facilities?fideicomiso_id=${selected.id}`, payload);
            toast.success(`Facility ${form.numero} creada · estado ratificada`);
            setOpen(false);
            setForm({
                numero: "", nombre_comercial: "", tipo: "senior", moneda: "MXN",
                monto_comprometido: "", fecha_efectiva: "", fecha_vencimiento: "",
                tasa_base: "TIIE", spread_pct: "", amortizacion: "bullet",
                lender_principal_persona_id: "",
            });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error creando facility");
        } finally {
            setBusy(false);
        }
    };

    const totales = useMemo(() => {
        const t = { count: rows.length, comprometido: 0, dispuesto: 0, en_default: 0 };
        rows.forEach((f) => {
            t.comprometido += Number(f.monto_comprometido || 0);
            t.dispuesto += Number(f.monto_dispuesto || 0);
            if (f.estado === "en_default") t.en_default += 1;
        });
        return t;
    }, [rows]);

    if (!selected) {
        return (
            <div className="p-10 text-muted-foreground text-sm" data-testid="facilities-empty-fid">
                Selecciona un fideicomiso en el header.
            </div>
        );
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="facilities-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        V1 · Garantía · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <LineChart className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Facilities
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Estructuras de financiamiento contra la cartera fideicomitida. Cada facility
                        tiene reglas de elegibilidad, covenants, waterfall y reportes independientes.
                    </p>
                </div>
                {canWrite && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="nueva-facility-btn">
                                <Plus className="h-4 w-4 mr-2" /> Nueva facility
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>Nueva facility</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3" data-testid="nueva-facility-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Número</Label>
                                        <Input required value={form.numero}
                                            onChange={(e) => setForm({ ...form, numero: e.target.value })}
                                            data-testid="facility-numero" />
                                    </div>
                                    <div>
                                        <Label>Nombre comercial</Label>
                                        <Input required value={form.nombre_comercial}
                                            onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}
                                            data-testid="facility-nombre" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <Label>Tipo</Label>
                                        <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                            <SelectTrigger data-testid="facility-tipo"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Moneda</Label>
                                        <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                                            <SelectTrigger data-testid="facility-moneda"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {MONEDAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Amortización</Label>
                                        <Select value={form.amortizacion} onValueChange={(v) => setForm({ ...form, amortizacion: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {AMORT.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label>Monto comprometido</Label>
                                    <Input type="number" step="0.01" required
                                        value={form.monto_comprometido}
                                        onChange={(e) => setForm({ ...form, monto_comprometido: e.target.value })}
                                        data-testid="facility-monto" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Fecha efectiva</Label>
                                        <Input type="date" required value={form.fecha_efectiva}
                                            onChange={(e) => setForm({ ...form, fecha_efectiva: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Vencimiento</Label>
                                        <Input type="date" required value={form.fecha_vencimiento}
                                            onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Tasa base</Label>
                                        <Input value={form.tasa_base}
                                            onChange={(e) => setForm({ ...form, tasa_base: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Spread (pct)</Label>
                                        <Input type="number" step="0.01" value={form.spread_pct}
                                            onChange={(e) => setForm({ ...form, spread_pct: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <Label>Lender principal (persona moral)</Label>
                                    <Select value={form.lender_principal_persona_id}
                                        onValueChange={(v) => setForm({ ...form, lender_principal_persona_id: v })}>
                                        <SelectTrigger data-testid="facility-lender"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                                        <SelectContent>
                                            {personas.map((p) => (
                                                <SelectItem key={p.id} value={p.id}>
                                                    {p.nombre_display || p.razon_social || p.id}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={busy} data-testid="submit-facility">
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear facility"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="facilities-kpis">
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                        <Layers className="h-3 w-3" /> Facilities
                    </div>
                    <div className="kpi-value mt-2">{totales.count}</div>
                </div>
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                        <Coins className="h-3 w-3" /> Comprometido
                    </div>
                    <div className="kpi-value mt-2 text-2xl">{money(totales.comprometido)}</div>
                </div>
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                        <Landmark className="h-3 w-3" /> Dispuesto
                    </div>
                    <div className="kpi-value mt-2 text-2xl">{money(totales.dispuesto)}</div>
                </div>
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                        {totales.en_default > 0 ? (
                            <ShieldAlert className="h-3 w-3 text-[color:var(--brand-coral)]" />
                        ) : (
                            <ShieldCheck className="h-3 w-3 text-[color:var(--brand-exito)]" />
                        )}
                        En default
                    </div>
                    <div className="kpi-value mt-2">{totales.en_default}</div>
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="facilities-table">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Moneda</th>
                            <th className="text-right">Comprometido</th>
                            <th className="text-right">Dispuesto</th>
                            <th>Vencimiento</th>
                            <th>Estado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((f) => (
                            <tr key={f.id} data-testid={`facility-row-${f.numero}`}>
                                <td className="tabular font-medium">{f.numero}</td>
                                <td className="text-sm">{f.nombre_comercial}</td>
                                <td><span className="badge-gold">{f.tipo}</span></td>
                                <td className="text-xs">{f.moneda}</td>
                                <td className="text-right tabular">{money(f.monto_comprometido, f.moneda)}</td>
                                <td className="text-right tabular">{money(f.monto_dispuesto, f.moneda)}</td>
                                <td className="tabular text-xs">{f.fecha_vencimiento}</td>
                                <td>
                                    <span className={ESTADO_BADGE[f.estado] || "badge-gold"}>{f.estado}</span>
                                </td>
                                <td>
                                    <Link to={`/v1/facilities/${f.id}`}>
                                        <Button size="sm" variant="ghost" data-testid={`open-facility-${f.numero}`}>
                                            Abrir <ArrowUpRight className="h-3 w-3 ml-1" />
                                        </Button>
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={9} className="text-center py-10 text-muted-foreground">
                                    Sin facilities en este fideicomiso. {canWrite && "Crea una con “Nueva facility”."}
                                </td>
                            </tr>
                        )}
                        {loading && (
                            <tr><td colSpan={9} className="text-center py-6">
                                <Loader2 className="h-4 w-4 animate-spin inline" />
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
